// Monkeypatch to trace setAttribute on null errors
if (typeof window !== "undefined") {
  const originalSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function (...args) {
    if (this === null) {
      console.trace('⚠️ setAttribute called on null:', args);
    }
    return originalSetAttribute.apply(this, args);
  };
}

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom'; // Import BrowserRouter
import App from './App'; // Import the App component
import './styles/main.scss'; // Import SCSS styles
import './i18n'; // Import the i18n configuration

// Import Geist Sans font from Fontsource
import '@fontsource/geist-sans/300.css'; // Light
import '@fontsource/geist-sans/400.css'; // Regular
import '@fontsource/geist-sans/700.css'; // Bold
import '@fontsource/geist-sans/900.css'; // Black

// Simple loading fallback component
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0f1a', color: 'white' }}>
    Loading translations...
  </div>
);

// Global root reference
let root = null;

// Function to safely remove loader
const removeLoader = () => {
  try {
    console.log('[Loader Debug] Attempting to remove loader...');
    const loader = document.getElementById('loader');
    
    if (loader) {
      console.log('[Loader Debug] Found loader element, adding fade-out class...');
      loader.classList.add('fade-out');
      
      const handleTransitionEnd = () => {
        try {
          console.log('[Loader Debug] Transition ended, removing loader...');
          if (document.body.contains(loader)) {
            loader.remove();
            console.log('[Loader Debug] Loader removed successfully');
          }
        } catch (error) {
          console.error('[Loader Debug] Error removing loader:', error);
        }
      };
      
      loader.addEventListener('transitionend', handleTransitionEnd);
      return () => loader.removeEventListener('transitionend', handleTransitionEnd);
    } else {
      console.log('[Loader Debug] No loader element found');
    }
  } catch (error) {
    console.error('[Loader Debug] Error in removeLoader:', error);
  }
};

// Function to initialize the app
const initializeApp = () => {
  try {
    console.log('[App Init] DOM Content Loaded, initializing app...');
    
    // Ensure root element exists
    const rootElement = document.getElementById('root');
    if (!rootElement) {
      console.log('[App Init] Creating root element...');
      const newRoot = document.createElement('div');
      newRoot.id = 'root';
      document.body.appendChild(newRoot);
    }

    // Prevent multiple initializations
    if (root) {
      console.log('[App Init] React root already exists, skipping initialization');
      return;
    }

    console.log('[App Init] Creating React root...');
    root = ReactDOM.createRoot(document.getElementById('root'));

    console.log('[App Init] Rendering app...');
    root.render(
      <React.StrictMode>
        <BrowserRouter> {/* Wrap everything in BrowserRouter */} 
          <Suspense fallback={<LoadingFallback />}> {/* Wrap App in Suspense */}
            <App />
          </Suspense>
        </BrowserRouter>
      </React.StrictMode>
    );
    console.log('[App Init] App rendered successfully');

    // Remove loader after app is rendered
    removeLoader();
  } catch (error) {
    console.error('[App Init] Error during initialization:', error);
  }
};

// Function to ensure DOM is ready
const ensureDOMReady = () => {
  return new Promise((resolve) => {
    if (document.readyState === 'loading') {
      console.log('[App Init] DOM still loading, waiting for DOMContentLoaded...');
      document.addEventListener('DOMContentLoaded', () => resolve());
    } else {
      console.log('[App Init] DOM already loaded, resolving immediately...');
      resolve();
    }
  });
};

// Initialize app with proper DOM ready check
ensureDOMReady().then(() => {
  // Small delay to ensure all DOM operations are complete
  setTimeout(initializeApp, 0);
});
